/**
 * navConfig.js — shared navigation structure and RBAC config for Layout.
 * Extracted from Layout.js to reduce file size.
 */
import {
  LayoutDashboard, ShoppingCart, Package, AlertTriangle, Receipt, Landmark,
  ClipboardList, DollarSign, Upload, ArrowDownUp, ArrowRightLeft,
  Stethoscope, RefreshCw, Megaphone, FileImage, MessageSquare,
  Users, Brain, Sparkles, TrendingUp, Copy, Activity, Truck, Trophy,
  Building2, FileText, UserCog, Wallet, Handshake, Shield,
  BarChart3, Banknote, Wrench, Monitor, Server, CreditCard,
  Settings, HelpCircle, Boxes, RotateCcw, CloudOff, FolderOpen, Bot, ShieldAlert, MessageCircle,
} from 'lucide-react';

export const ROLE_PERMISSIONS = {
  super_admin: [
    'dashboard', 'pos', 'sales-history', 'inventory', 'pricing', 'bulk-import', 'stock-adjustments',
    'patients', 'drug-interactions', 'prescriptions', 'reorder', 'demand-forecast',
    'suppliers', 'purchases', 'finance', 'insurance', 'corporate', 'billing',
    'users', 'tenants', 'system', 'settings', 'reports', 'help', 'hr', 'payroll'
  ],
  admin: [
    'dashboard', 'pos', 'sales-history', 'inventory', 'pricing', 'bulk-import', 'stock-adjustments',
    'patients', 'drug-interactions', 'prescriptions', 'reorder', 'demand-forecast',
    'suppliers', 'purchases', 'finance', 'insurance', 'corporate', 'billing',
    'users', 'tenants', 'system', 'settings', 'reports', 'help', 'hr', 'payroll'
  ],
  pharmacist: [
    'dashboard', 'pos', 'sales-history', 'inventory',
    'patients', 'drug-interactions', 'prescriptions', 'reorder',
    'suppliers-view', 'purchases-view', 'reports', 'help'
  ],
  inventory_manager: [
    'dashboard', 'inventory', 'pricing', 'bulk-import', 'stock-adjustments',
    'suppliers', 'purchases', 'reorder', 'demand-forecast', 'patients-view', 'reports', 'help'
  ],
  finance: [
    'dashboard', 'sales-history', 'finance', 'insurance', 'corporate', 'billing',
    'inventory-view', 'suppliers-view', 'purchases-view', 'reports', 'tenants', 'help', 'payroll'
  ],
  hr_manager: ['dashboard', 'hr', 'payroll', 'help'],
  accountant: [
    'dashboard', 'sales-history', 'finance', 'insurance', 'corporate', 'billing',
    'inventory-view', 'suppliers-view', 'purchases-view', 'reports', 'help', 'payroll'
  ],
  finance_admin: [
    'dashboard', 'sales-history', 'finance', 'insurance', 'corporate', 'billing',
    'inventory', 'suppliers', 'purchases', 'reports', 'help',
    'hr', 'payroll', 'settings', 'system', 'tenants'
  ],
  cashier: ['dashboard', 'pos', 'sales-history', 'prescriptions-scan', 'patients-view', 'help'],
};

export const ROLE_LABELS = {
  super_admin:      { label: 'Super Admin',    color: 'bg-purple-500' },
  admin:            { label: 'Admin',           color: 'bg-purple-500' },
  pharmacist:       { label: 'Pharmacist',      color: 'bg-blue-500'   },
  inventory_manager:{ label: 'Inventory Mgr',   color: 'bg-green-500'  },
  finance:          { label: 'Finance',         color: 'bg-amber-500'  },
  hr_manager:       { label: 'HR Manager',      color: 'bg-pink-500'   },
  accountant:       { label: 'Accountant',      color: 'bg-amber-500'  },
  finance_admin:    { label: 'Finance Admin',   color: 'bg-orange-600' },
  cashier:          { label: 'Cashier',         color: 'bg-gray-500'   },
};

export const NAV_GROUPS = [
  {
    id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, module: null,
    items: [{ path: '/dashboard', label: 'Overview', icon: LayoutDashboard, key: 'dashboard' }],
    single: true,
  },
  {
    id: 'sales', label: 'Sales', icon: ShoppingCart, module: 'sales',
    items: [
      { path: '/pos',            label: 'Point of Sale',  icon: ShoppingCart, key: 'pos'           },
      { path: '/sales-history',  label: 'Sales History',  icon: Receipt,      key: 'sales-history' },
      { path: '/daily-summary',  label: 'Daily Summary',  icon: ClipboardList,key: 'sales-history' },
      { path: '/customers/bills',label: 'Layaways',       icon: Banknote,     key: 'patients'      },
    ],
  },
  {
    id: 'inventory', label: 'Inventory', icon: Boxes, module: 'inventory',
    items: [
      { path: '/inventory',         label: 'Products',      icon: Package,       key: 'inventory'         },
      { path: '/expired-products',  label: 'Expired Items', icon: AlertTriangle, key: 'inventory'         },
      { path: '/near-expiry',       label: 'Near-Expiry',   icon: AlertTriangle, key: 'inventory'         },
      { path: '/pricing',           label: 'Pricing',       icon: DollarSign,    key: 'pricing'           },
      { path: '/bulk-import',       label: 'Bulk Import',   icon: Upload,        key: 'bulk-import'       },
      { path: '/stock-adjustments', label: 'Stock Adjust',  icon: ArrowDownUp,   key: 'stock-adjustments' },
      { path: '/stock-take',        label: 'Stock Take',    icon: ClipboardList, key: 'inventory'         },
      { path: '/inventory/pack-counts', label: 'Pack Counts', icon: ClipboardList, key: 'inventory' },
      { path: '/inventory/shelf-moves', label: 'Shelf Moves', icon: ArrowRightLeft, key: 'inventory' },
      { path: '/inventory/stock-insights', label: 'Stock Insights', icon: Boxes, key: 'inventory' },
      { path: '/inventory/printer-profiles', label: 'Label Profiles', icon: Package, key: 'inventory' },
      { path: '/sync-conflicts',    label: 'Sync Review',   icon: CloudOff,      key: 'stock-adjustments' },
      { path: '/stock-transfers',   label: 'Transfers',     icon: ArrowRightLeft,key: 'purchases'         },
      { path: '/catalog',           label: 'Catalog',       icon: Boxes,         key: 'inventory'         },
    ],
  },
  {
    id: 'clinical', label: 'Clinical', icon: Stethoscope, module: 'clinical',
    items: [
      { path: '/refills',              label: 'Refills',             icon: RefreshCw,     key: 'patients'       },
      { path: '/marketing',            label: 'Marketing',       icon: Megaphone,     key: 'patients'       },
      { path: '/prescriptions',        label: 'Rx Scanner',          icon: FileImage,     key: 'prescriptions'  },
      { path: '/drug-interactions',    label: 'Drug Check',          icon: AlertTriangle, key: 'drug-interactions'},
      { path: '/controlled-registry',  label: 'Controlled Register', icon: Shield,        key: 'prescriptions'  },
    ],
  },
  {
    id: 'customer_engagement', label: 'Client Engagement', icon: MessageSquare, module: 'customer_engagement',
    items: [
      { path: '/customers/records',       label: 'Client Admin',      icon: Users,          key: 'patients' },
      { path: '/customers/verify',        label: 'ID Verification',   icon: Shield,         key: 'patients' },
      { path: '/customers/orders',        label: 'Online orders',     icon: ShoppingCart,    key: 'patients' },
      { path: '/customers/prescriptions', label: 'Prescriptions',     icon: FileImage,       key: 'patients' },
      { path: '/customers/refills',       label: 'Refills',           icon: RefreshCw,       key: 'patients' },
      { path: '/customers/conversations', label: 'Response Team',     icon: MessageSquare,   key: 'patients' },
      { path: '/whatsapp-inbox',          label: 'WhatsApp Inbox',    icon: MessageCircle,   key: 'patients' },
      { path: '/customers/handover',      label: 'Handover',          icon: Banknote,        key: 'patients' },
    ],
  },
  {
    id: 'ai_tools', label: 'AI Tools', icon: Brain, module: 'ai_tools',
    items: [
      { path: '/duti-ai',              label: 'Duti AI',         icon: Bot,           key: 'dashboard'      },
      { path: '/ai-assistant',         label: 'Duti AI Chat',    icon: Sparkles,      key: 'reorder'        },
      { path: '/reorder',              label: 'AI Reorder',      icon: Sparkles,      key: 'reorder'        },
      { path: '/demand-forecast',      label: 'Demand Forecast', icon: TrendingUp,    key: 'demand-forecast'},
      { path: '/fraud-analysis',       label: 'Fraud Detection', icon: AlertTriangle, key: 'system'         },
      { path: '/duplicate-detection',  label: 'Duplicate Finder',icon: Copy,          key: 'reorder'        },
      { path: '/ai-usage',             label: 'AI Usage',        icon: Activity,      key: 'system'         },
    ],
  },
  {
    id: 'procurement', label: 'Procurement', icon: Truck, module: 'procurement',
    items: [
      { path: '/suppliers',          label: 'Suppliers',       icon: Truck,        key: 'suppliers' },
      { path: '/suppliers/scorecard',label: 'Scorecard',       icon: Trophy,       key: 'suppliers' },
      { path: '/purchase-planner',   label: 'Planner',         icon: Sparkles,     key: 'purchases' },
      { path: '/purchases',          label: 'Purchases',       icon: ClipboardList,key: 'purchases' },
      { path: '/supplier-returns',   label: 'Supplier Returns',icon: RotateCcw,    key: 'purchases' },
    ],
  },
  {
    id: 'finance', label: 'Finance', icon: DollarSign, module: 'finance',
    items: [
      { path: '/finance',                          label: 'Overview',         icon: DollarSign,   key: 'finance' },
      { path: '/finance/expenses',                  label: 'Expenses',         icon: Receipt,      key: 'finance' },
      { path: '/finance/assets',                    label: 'Fixed Assets',     icon: Landmark,     key: 'finance' },
      { path: '/finance/files',                     label: 'File Storage',     icon: FolderOpen,   key: 'finance' },
      { path: '/finance/ledger/accounts',          label: 'Chart of Accounts',icon: ClipboardList,key: 'finance' },
      { path: '/finance/ledger/journals',          label: 'Journal Entries',  icon: FileText,     key: 'finance' },
      { path: '/finance/ledger/trial-balance',     label: 'Trial Balance',    icon: FileText,     key: 'finance' },
      { path: '/finance/ledger/profit-loss',       label: 'Profit & Loss',    icon: FileText,     key: 'finance' },
      { path: '/finance/ledger/balance-sheet',     label: 'Balance Sheet',    icon: FileText,     key: 'finance' },
      { path: '/finance/ledger/period-close',      label: 'Period Close',     icon: FileText,     key: 'finance' },
    ],
  },
  {
    id: 'hr', label: 'HR & Payroll', icon: UserCog, module: 'hr',
    items: [
      { path: '/hr/employees',  label: 'Employees',      icon: Users,        key: 'hr'      },
      { path: '/hr/attendance', label: 'Attendance',     icon: ClipboardList,key: 'hr'      },
      { path: '/hr/leave',      label: 'Leave Requests', icon: FileText,     key: 'hr'      },
      { path: '/hr/payroll',    label: 'Payroll Runs',   icon: Wallet,       key: 'payroll' },
    ],
  },
  {
    id: 'crm', label: 'CRM & Billing', icon: Handshake, module: 'crm',
    items: [
      { path: '/insurance-providers', label: 'Insurance', icon: Shield,   key: 'insurance' },
      { path: '/corporate-clients',   label: 'Corporate',  icon: Building2,key: 'corporate' },
      { path: '/loyalty',             label: 'Loyalty',    icon: Sparkles, key: 'corporate' },
      { path: '/billing',             label: 'Billing',    icon: FileText, key: 'billing'   },
    ],
  },
  {
    id: 'reports', label: 'Reports & Analytics', icon: BarChart3, module: null,
    items: [
      { path: '/reports',            label: 'All Reports',       icon: BarChart3,    key: 'reports'       },
      { path: '/portal-engagement',  label: 'Portal Engagement', icon: Activity,     key: 'reports'       },
      { path: '/recall-report',      label: 'Branch Recalls',    icon: ShieldAlert,  key: 'users'         },
      { path: '/shift-handovers',    label: 'Shift Handovers',   icon: Banknote,     key: 'sales-history' },
    ],
  },
  {
    id: 'admin', label: 'Branches & admin', icon: Wrench, module: 'admin',
    items: [
      { path: '/users',                      label: 'User Mgmt',       icon: Users,        key: 'users'    },
      { path: '/terminals',                  label: 'Terminals',        icon: Monitor,      key: 'users'    },
      { path: '/sms-log',                    label: 'SMS delivery log', icon: MessageSquare, key: 'settings' },
      { path: '/branches',                   label: 'Branches',         icon: Building2,    key: 'tenants'  },
      { path: '/branch-performance',         label: 'Head office view', icon: BarChart3,    key: 'tenants'  },
      { path: '/system',                     label: 'System',           icon: Server,       key: 'system'   },
      { path: '/admin/permissions-audit',    label: 'Permission Audit', icon: Shield,       key: 'users'    },
      { path: '/subscription',               label: 'Subscription',     icon: CreditCard,   key: 'settings' },
      { path: '/standing',                   label: 'DutiLyne standing', icon: Sparkles,    key: 'settings' },
      { path: '/privacy-requests',           label: 'Privacy requests', icon: Shield,       key: 'patients' },
      { path: '/settings',                   label: 'Settings',         icon: Settings,     key: 'settings' },
      { path: '/help',                       label: 'Help & Tutorials', icon: HelpCircle,   key: 'help'     },
    ],
  },
];

/** RBAC check — returns true if the given role can access the nav key. */
export const hasAccess = (userRole, navKey) => {
  const allowed = ROLE_PERMISSIONS[userRole] || ROLE_PERMISSIONS.cashier;
  return allowed.includes(navKey) ||
         allowed.includes(`${navKey}-view`) ||
         allowed.includes(`${navKey}-scan`);
};
